import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, CalendarDays, MapPin, Users, ArrowLeft, CalendarPlus, Radio, PlayCircle, Eye } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toEmbedUrl } from "@/lib/video";
import { toast } from "sonner";
import { SocialShare } from "@/components/SocialShare";
import Seo, { stripHtml } from "@/components/Seo";

import RichContent from "@/components/RichContent";
import { toPlainText } from "@/components/RichContent";
function pad(n) { return String(n).padStart(2, "0"); }
function icsStamp(d) {
  return `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}00Z`;
}
function calWindow(startsAt) {
  const start = startsAt ? new Date(startsAt) : new Date();
  const end = new Date(start.getTime() + 60 * 60 * 1000);
  return { start, end };
}
function googleCalUrl(ev) {
  const { start, end } = calWindow(ev.starts_at);
  const p = new URLSearchParams({
    action: "TEMPLATE", text: ev.title || "Gathering",
    dates: `${icsStamp(start)}/${icsStamp(end)}`,
    details: toPlainText(ev.description || ""), location: ev.location || "",
  });
  return `https://calendar.google.com/calendar/render?${p.toString()}`;
}
function buildIcs(events) {
  const lines = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//EnhancingYourWorld//EN"];
  for (const ev of events) {
    const { start, end } = calWindow(ev.starts_at);
    lines.push("BEGIN:VEVENT", `UID:${ev.id}@enhancingyourworld`, `DTSTAMP:${icsStamp(new Date())}`,
      `DTSTART:${icsStamp(start)}`, `DTEND:${icsStamp(end)}`,
      `SUMMARY:${(ev.title || "").replace(/\n/g, " ")}`,
      `DESCRIPTION:${toPlainText(ev.description || "").replace(/\n/g, " ")}`,
      `LOCATION:${(ev.location || "").replace(/\n/g, " ")}`, "END:VEVENT");
  }
  lines.push("END:VCALENDAR");
  return lines.join("\r\n");
}
function saveIcs(ics, name) {
  const url = window.URL.createObjectURL(new Blob([ics], { type: "text/calendar" }));
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  window.URL.revokeObjectURL(url);
}
function downloadIcs(ev) {
  saveIcs(buildIcs([ev]), `${(ev.title || "event").replace(/\s+/g, "-").toLowerCase()}.ics`);
}

export default function EventPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [calOpen, setCalOpen] = useState(false);
  const { data: ev, isLoading, error } = useQuery({
    queryKey: ["event", id],
    queryFn: () => api.get(`/group-events/${id}`).then((r) => r.data),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["event", id] });
  // Old ID links keep working and move to the readable address.
  useEffect(() => {
    if (ev?.slug && id === ev.id) nav(`/gatherings/${ev.slug}`, { replace: true });
  }, [ev, id, nav]);

  const addSeries = async () => {
    setCalOpen(false);
    try {
      const { data } = await api.get(`/group-events/${ev.id}/series`);
      saveIcs(buildIcs(data.events), `${(ev.title || "series").replace(/\s+/g, "-").toLowerCase()}-series.ics`);
      toast.success(`Added ${data.events.length} dates to your calendar file`);
    } catch { toast.error("Could not build the series file."); }
  };

  const rsvp = async (status) => {
    if (!user) { nav("/login"); return; }
    try { await api.post(`/group-events/${ev.id}/rsvp`, { status }); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const join = async () => {
    try { await api.post(`/groups/${ev.group.id}/join`); toast.success(`Welcome to ${ev.group.name}!`); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (isLoading) return <div className="grid place-items-center py-40"><Loader2 className="animate-spin text-[#144F75]" size={28} /></div>;
  if (error || !ev) return <div className="max-w-xl mx-auto px-6 py-32 text-center text-[#4A5D6B]" data-testid="event-not-found">This gathering could not be found.</div>;

  const fmt = ev.starts_at ? `${new Date(ev.starts_at).toLocaleString("en-US", { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: "America/New_York" })} ET` : "Date to be announced";

  return (
    <div className="max-w-2xl mx-auto px-6 lg:px-10 py-12" data-testid="event-page">
      <Seo title={ev.title} description={stripHtml(ev.description || "") || `A community gathering${ev.group ? ` hosted by ${ev.group.name}` : ""} at Enhancing Your World.`}
        image={ev.cover_image} path={`/gatherings/${ev.slug || ev.id}`} type="article"
        jsonLd={{
          "@context": "https://schema.org", "@type": "Event", name: ev.title,
          startDate: ev.starts_at || undefined,
          description: stripHtml(ev.description || "", 300) || undefined,
          image: ev.cover_image || undefined,
          eventStatus: "https://schema.org/EventScheduled",
          eventAttendanceMode: ev.is_live || ev.has_stream ? "https://schema.org/OnlineEventAttendanceMode" : "https://schema.org/OfflineEventAttendanceMode",
          location: ev.location ? { "@type": "Place", name: ev.location } : (ev.has_stream ? { "@type": "VirtualLocation", url: `${window.location.origin}/gatherings/${ev.slug || ev.id}` } : undefined),
          organizer: ev.group ? { "@type": "Organization", name: ev.group.name } : undefined,
        }} />
      {ev.group && <Link to={`/community/groups/${ev.group.slug || ev.group.id}`} data-testid="event-group-link" className="text-sm text-[#144F75] inline-flex items-center gap-1 mb-6 hover:underline"><ArrowLeft size={14} /> {ev.group.name}</Link>}
      <div className="eyw-card overflow-hidden">
        {ev.cover_image && <img src={ev.cover_image} alt={ev.title} className="w-full h-56 md:h-64 object-cover" data-testid="event-cover" />}
        <div className="p-8">
        <span className="text-xs uppercase tracking-wide text-[#8FA89B]">Community gathering</span>
        <h1 className="font-serif text-3xl md:text-4xl text-[#1A242B] mt-1 mb-5" data-testid="event-title">{ev.title}</h1>
        <div className="space-y-2 mb-6">
          <p className="text-[#144F75] flex items-center gap-2"><CalendarDays size={17} /> {fmt}</p>
          {ev.location && <p className="text-[#4A5D6B] flex items-center gap-2"><MapPin size={17} /> {ev.location}</p>}
          {ev.group && <p className="text-[#4A5D6B] flex items-center gap-2"><Users size={17} /> Hosted by {ev.group.name}</p>}
        </div>
        {ev.description && <RichContent text={ev.description} className="text-[#1A242B] mb-6 leading-relaxed" testid="event-description" />}

        {ev.is_live && ev.live_url ? (
          <div className="mb-6" data-testid="event-live-block">
            <div className="flex items-center gap-3 mb-2">
              <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-600"><span className="w-2 h-2 rounded-full bg-red-600 animate-pulse" /> LIVE NOW</div>
              {ev.viewers > 0 && <span className="inline-flex items-center gap-1 text-xs text-[#4A5D6B]" data-testid="event-live-viewers"><Eye size={12} /> {ev.viewers} watching</span>}
            </div>
            {toEmbedUrl(ev.live_url) !== ev.live_url ? (
              <div className="aspect-video rounded-lg overflow-hidden bg-black" data-testid="event-live-embed">
                <iframe src={toEmbedUrl(ev.live_url)} title="Live stream" className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              </div>
            ) : (
              <a href={ev.live_url} target="_blank" rel="noreferrer" data-testid="event-live-link" className="eyw-btn !py-2 text-sm w-full justify-center"><Radio size={15} /> Join the live stream</a>
            )}
          </div>
        ) : ev.starting_soon ? (
          <div className="mb-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#B5794E] bg-[#D98E73]/15 px-4 py-2 rounded-full" data-testid="event-starting-soon">
            <Radio size={14} /> Starting soon — {ev.rsvp_counts?.going || 0} people waiting
          </div>
        ) : ev.replay_url ? (
          <div className="mb-6" data-testid="event-replay-block">
            <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#144F75] mb-2"><PlayCircle size={15} /> Watch the replay</div>
            {toEmbedUrl(ev.replay_url) !== ev.replay_url ? (
              <div className="aspect-video rounded-lg overflow-hidden bg-black" data-testid="event-replay-embed">
                <iframe src={toEmbedUrl(ev.replay_url)} title="Replay" className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              </div>
            ) : (
              <a href={ev.replay_url} target="_blank" rel="noreferrer" data-testid="event-replay-link" className="eyw-btn-ghost !py-2 text-sm w-full justify-center"><PlayCircle size={15} /> Watch the replay</a>
            )}
          </div>
        ) : (ev.is_live && ev.has_stream) || (!ev.is_live && !ev.starting_soon && ev.has_replay) ? (
          <div className="mb-6 rounded-lg border border-[#E5E0D8] bg-[#F9F7F3] px-4 py-3 text-sm text-[#4A5D6B] flex items-center gap-2" data-testid="event-stream-locked">
            <Radio size={15} className="text-[#144F75]" />
            {ev.is_live ? "This gathering is live now." : "A replay is available."} Join {ev.group?.name || "the group"} to watch.
          </div>
        ) : null}

        <div className="flex items-center gap-3 text-sm text-[#8FA89B] mb-6">
          <span data-testid="event-going-count">{ev.rsvp_counts?.going || 0} going</span>
          <span>·</span>
          <span>{ev.rsvp_counts?.maybe || 0} maybe</span>
        </div>

        {ev.is_member ? (
          <div className="flex flex-wrap gap-2 mb-4">
            {[{ k: "going", l: "I'm going" }, { k: "maybe", l: "Maybe" }, { k: "no", l: "Can't make it" }].map((o) => (
              <button key={o.k} onClick={() => rsvp(o.k)} data-testid={`event-rsvp-${o.k}`}
                className={`px-4 py-2 rounded-full border text-sm transition-colors ${ev.my_rsvp === o.k ? "bg-[#144F75] text-white border-[#144F75]" : "border-[#E5E0D8] text-[#4A5D6B] hover:border-[#144F75]"}`}>{o.l}</button>
            ))}
          </div>
        ) : ev.can_join ? (
          <button onClick={join} data-testid="event-join-group" className="eyw-btn mb-4">Join {ev.group?.name} to RSVP</button>
        ) : !user ? (
          <Link to="/login" className="eyw-btn mb-4">Sign in to RSVP</Link>
        ) : (
          <Link to="/join" className="eyw-btn mb-4">Become a member to RSVP</Link>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <button onClick={() => setCalOpen((v) => !v)} data-testid="event-addcal-btn" className="eyw-btn-ghost !py-2 text-sm"><CalendarPlus size={15} /> Add to calendar</button>
            {calOpen && (
              <div className="absolute z-20 mt-2 w-52 bg-white rounded-xl shadow-lg border border-[#E5E0D8] overflow-hidden" data-testid="event-addcal-menu">
                <a href={googleCalUrl(ev)} target="_blank" rel="noreferrer" onClick={() => setCalOpen(false)} data-testid="event-addcal-google" className="block px-4 py-2.5 text-sm text-[#1A242B] hover:bg-[#144F75]/[0.05]">Google Calendar</a>
                <button onClick={() => { downloadIcs(ev); setCalOpen(false); }} data-testid="event-addcal-ics" className="block w-full text-left px-4 py-2.5 text-sm text-[#1A242B] hover:bg-[#144F75]/[0.05]">Apple / Outlook (.ics)</button>
                {ev.series_id && <button onClick={addSeries} data-testid="event-addcal-series" className="block w-full text-left px-4 py-2.5 text-sm text-[#1A242B] hover:bg-[#144F75]/[0.05] border-t border-[#E5E0D8]">Add all dates (.ics)</button>}
              </div>
            )}
          </div>
          <SocialShare path={`/gatherings/${ev.slug || ev.id}`} ogPath={`/api/share/gathering/${ev.id}`} title={ev.title} testid="event-share" label="Share this gathering" />
        </div>
        </div>
      </div>
    </div>
  );
}
